import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  SearchBar,
  LoadingSpinner,
  EmptyState,
  ListContainer,
  Pagination,
  Button,
  Badge,
  ErrorMessage,
  ConfirmationModal,
  SuccessMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell
} from '../components/common';

const Buildings = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, buildingId: null, buildingName: '' });
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchBuildings();
  }, [currentPage]);

  const fetchBuildings = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
        ...(searchTerm && { searchString: searchTerm })
      };

      const response = await api.get('/Building', { params });
      setBuildings(response.data.items || []);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      setError(extractErrorMessage(err));
      console.error('Error fetching buildings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchBuildings();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCreateBuilding = () => {
    navigate('/buildings/create');
  };

  const handleViewBuilding = (buildingId) => {
    navigate(`/buildings/${buildingId}`);
  };

  const handleDeleteBuilding = (building) => {
    setDeleteModal({
      isOpen: true,
      buildingId: building.id,
      buildingName: building.name || 'Unnamed Building'
    });
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`/Building/${deleteModal.buildingId}`);
      setSuccess('Building deleted successfully');
      setDeleteModal({ isOpen: false, buildingId: null, buildingName: '' });
      fetchBuildings();
    } catch (err) {
      console.error('Error deleting building:', err);
      showError(err);
    } finally {
      setDeleting(false);
    }
  };

  const getRegisterTypeInfo = (registerType) => {
    const types = {
      0: { label: 'Open', variant: 'success' },
      1: { label: 'Request', variant: 'warning' },
      2: { label: 'Invite Only', variant: 'info' },
      3: { label: 'Password', variant: 'secondary' }
    };
    return types[registerType] || types[3];
  };

  if (loading && buildings.length === 0) {
    return <LoadingSpinner size="large" />;
  }

  if (error && buildings.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Buildings" />
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Buildings"
        action={isAdmin ? {
          label: "Create Building",
          onClick: handleCreateBuilding
        } : undefined}
      />

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      {success && (
        <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />
      )}

      <SearchBar
        placeholder="Search buildings..."
        value={searchTerm}
        onChange={setSearchTerm}
        onSearch={handleSearch}
      />

      <ListContainer>
        {buildings.length === 0 ? (
          <EmptyState message="No buildings found" icon="🏢" />
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Building</TableCell>
                  <TableCell header>Username</TableCell>
                  <TableCell header>Registration</TableCell>
                  <TableCell header>Modules</TableCell>
                  <TableCell header>
                    <span className="sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {buildings.map((building) => (
                  <TableRow key={building.id} clickable onClick={() => handleViewBuilding(building.id)}>
                    <TableCell>
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                        {building.name || 'Unnamed Building'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-500">
                        {building.userName || <span className="text-slate-400 italic">N/A</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getRegisterTypeInfo(building.registerType).variant}
                        size="small"
                      >
                        {getRegisterTypeInfo(building.registerType).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-700">
                        {building.selectedModules?.length || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end space-x-2 pr-4">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewBuilding(building.id);
                          }}
                          variant="ghost"
                          size="small"
                        >
                          View
                        </Button>
                        {isAdmin && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBuilding(building);
                            }}
                            variant="danger"
                            size="small"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-4 flex justify-between items-center px-2">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </ListContainer>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, buildingId: null, buildingName: '' })}
        onConfirm={confirmDelete}
        title="Delete Building"
        message={`Are you sure you want to delete "${deleteModal.buildingName}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default Buildings;